import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import SelecteurProduction, {
  productionParDefaut,
} from '../../shared/components/SelecteurProduction';
import StateGuard from '../../shared/components/StateGuard';
import LimiteErreur from '../../shared/components/LimiteErreur';
import { useApiData } from '../../shared/hooks/useFetch';
import { api } from '../../shared/api/api';
import { RCP_OPTIONS } from '../../shared/constants';
import EauChart from '../../shared/charts/EauChart';
import IrrigationCumuleeChart from '../../shared/charts/IrrigationCumuleeChart';
import IrrigationJournalierChart from '../../shared/charts/IrrigationJournalierChart';

/**
 * 2035-2050 / Eau et irrigation / Besoin en eau.
 *
 * Reprend les trois panneaux d'EauTab (DiagoEau). En élevage, seul le premier
 * est affiché : un atelier d'élevage n'est pas irrigué, les deux panneaux
 * d'irrigation n'auraient pas de sens.
 */
export default function BesoinEnEau() {
  const { idExploit } = useOAD();
  const [production, setProduction] = React.useState(productionParDefaut);
  const [rcp, setRcp] = React.useState(RCP_OPTIONS[0].value);

  const elevage = production.type === 'elevage';

  const parametres = React.useMemo(
    () => ({
      idExploitation: idExploit,
      type: production.type,
      idProduction: production.id,
      semis: production.semis,
      alimentation: production.alimentation,
      abreuvement: production.abreuvement,
      rcp,
    }),
    [idExploit, production, rcp]
  );

  const { data, loading, error } = useApiData(api.getEauIrrigation, parametres, {
    skip: !idExploit || !production.id,
  });

  const eauXMax = data?.eau?.length
    ? Math.max(...data.eau.map((d) => new Date(d.date_affichage).getTime()))
    : null;

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="eau-irrigation"
        niveau3="besoin-en-eau"
        idExploit={idExploit}
      >
        <SelecteurProduction valeur={production} onChange={setProduction} />

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-besoin-eau"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <LimiteErreur cle={`${production.id}-${rcp}`}>
        <StateGuard
          loading={loading}
          error={error}
          empty={!data || (!data.eau?.length && !data.irrigation?.length)}
          emptyMessage="Pas de données eau/irrigation pour cette production."
        >
          {data && (
            <>
              <section className="oad-card">
                <h2 className="tdb-section-titre">
                  {elevage
                    ? 'Besoin en eau journalier des animaux'
                    : 'Besoin en eau journalier'}
                </h2>
                <EauChart data={data.eau} uniteElevage={elevage} />
              </section>

              {!elevage && (
                <>
                  <section className="oad-card">
                    <h2 className="tdb-section-titre">Besoin en irrigation cumulée</h2>
                    <IrrigationCumuleeChart data={data.irrigation} xMax={eauXMax} />
                  </section>

                  <section className="oad-card">
                    <h2 className="tdb-section-titre">
                      Besoin en irrigation cumulée (écart à 2020)
                    </h2>
                    <IrrigationJournalierChart data={data.irrigation} xMax={eauXMax} />
                  </section>
                </>
              )}
            </>
          )}
        </StateGuard>
        </LimiteErreur>
      </main>
    </div>
  );
}
